import React, { useEffect, useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Package, Heart, Car, Wallet, BookUser, CreditCard, Briefcase, BadgeCheck, Clock, ChevronRight, Loader2 } from 'lucide-react';
import AccountLayout, { LinkCard } from '../components/AccountLayout';
import AccountSettings from '../components/AccountSettings';
import NotificationsCenter from '../components/NotificationsCenter';
import MyAlertsSection from '../components/MyAlertsSection';
import ReferralSection from '../components/ReferralSection';
import AddressBook from '../components/AddressBook';
import BankDetails from '../components/BankDetails';
import WalletSection from '../components/WalletSection';
import ProAccountSection from '../components/ProAccountSection';
import MyVehiclesSection from '../components/MyVehiclesSection';
import MaintenanceBook from '../components/MaintenanceBook';
import { accountAPI } from '../services/api';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';
import SellerApplicationSection from '../components/seller/SellerApplicationSection';
import SellerListingsSection from '../components/seller/SellerListingsSection';
import SellerImportSection from '../components/seller/SellerImportSection';
import SellerFeedsSection from '../components/seller/SellerFeedsSection';
import SellerOrdersSection from '../components/seller/SellerOrdersSection';

const money = (v) => `${Number(v || 0).toFixed(2)} €`;

const Dashboard = () => {
  const { t } = useT();
  const [s, setS] = useState(null);
  useEffect(() => { accountAPI.summary().then(setS).catch(() => setS({})); }, []);
  if (!s) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-orange-500" /></div>;
  const pro = s.pro;
  return (
    <div className="space-y-6" data-testid="account-dashboard">
      {pro && (
        <div className={`rounded-xl px-5 py-3 flex items-center gap-3 text-sm ${pro.status === 'validated' ? 'bg-green-50 text-green-800 border border-green-200' : pro.status === 'pending' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="dashboard-pro-banner">
          {pro.status === 'validated' ? <BadgeCheck size={18} /> : <Clock size={18} />}
          <span className="flex-1">{pro.status === 'validated' ? t('acc.proBannerOk', { company: pro.company_name }) : pro.status === 'pending' ? t('acc.proBannerPending', { company: pro.company_name }) : t('acc.proBannerRefused')}</span>
          <Link to="/mon-compte/pro" className="underline">{t('acc.details')}</Link>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <LinkCard to="/mes-commandes" icon={Package} label={t('acc.orders')} value={s.orders_count ?? 0} hint={s.open_orders ? t('acc.openOrders', { n: s.open_orders, spent: money(s.total_spent) }) : t('acc.spent', { spent: money(s.total_spent) })} testId="dashboard-orders" />
        <LinkCard to="/mon-compte/depot" icon={Wallet} label={t('acc.walletCard')} value={money(s.wallet_balance)} hint={t('acc.walletHint')} testId="dashboard-wallet" />
        <LinkCard to="/liste-de-souhaits" icon={Heart} label={t('acc.wishlistCard')} value={s.wishlist_count ?? 0} hint={t('acc.wishlistHint')} testId="dashboard-wishlist" />
        <LinkCard to="/mon-compte/garage" icon={Car} label={t('acc.garage')} value={s.vehicles_count ?? 0} hint={t('acc.vehiclesHint')} testId="dashboard-vehicles" />
        <LinkCard to="/mon-compte/adresses" icon={BookUser} label={t('acc.addressesCard')} value={s.addresses_count ?? 0} hint={t('acc.addressesHint')} testId="dashboard-addresses" />
        <LinkCard to="/mon-compte/banque" icon={CreditCard} label={t('acc.bank')} value={t('acc.cards', { n: s.cards_count ?? 0 })} hint={s.has_iban ? t('acc.ibanYes') : t('acc.ibanNo')} testId="dashboard-bank" />
      </div>
      {s.last_order && (
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-last-order">
          <div><p className="text-xs uppercase tracking-wide text-gray-500">{t('acc.lastOrder')}</p><p className="font-semibold text-gray-900">{s.last_order.order_number} · {money(s.last_order.total)} · <span className="text-orange-600">{t(`status.${s.last_order.status}`)}</span></p></div>
          <Link to="/mes-commandes" className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1">{t('acc.viewOrders')} <ChevronRight size={14} /></Link>
        </div>
      )}
      {!pro && (
        <Link to="/mon-compte/pro" className="block bg-[#1e3a5f] text-white rounded-xl p-5 hover:bg-[#2d4a6f] transition-colors" data-testid="dashboard-pro-cta">
          <p className="font-semibold flex items-center gap-2"><Briefcase size={18} className="text-orange-400" /> {t('acc.proCta')}</p>
          <p className="text-sm text-gray-300 mt-1">{t('acc.proCtaSub')}</p>
        </Link>
      )}
    </div>
  );
};

const AccountPage = () => {
  const { t } = useT();
  usePageMeta({ title: t('acc.myAccount'), noindex: true });
  return (
    <Routes>
      <Route index element={<AccountLayout title={t('acc.dashboard')}><Dashboard /></AccountLayout>} />
      <Route path="garage" element={<AccountLayout title={t('acc.garage')}><MyVehiclesSection /></AccountLayout>} />
      <Route path="entretien" element={<AccountLayout title={t('acc.maintenanceTitle')}><MaintenanceBook /></AccountLayout>} />
      <Route path="pro" element={<AccountLayout title={t('acc.pro')}><ProAccountSection /></AccountLayout>} />
      <Route path="vendeur" element={<AccountLayout title={t('acc.seller')} wide><SellerApplicationSection /></AccountLayout>} />
      <Route path="vendeur/annonces" element={<AccountLayout title={t('acc.sellerListings')} wide><SellerListingsSection /></AccountLayout>} />
      <Route path="vendeur/import" element={<AccountLayout title={t('acc.sellerImport')} wide><SellerImportSection /></AccountLayout>} />
      <Route path="vendeur/synchronisation" element={<AccountLayout title={t('acc.sellerFeeds')} wide><SellerFeedsSection /></AccountLayout>} />
      <Route path="vendeur/commandes" element={<AccountLayout title={t('acc.sellerOrders')} wide><SellerOrdersSection /></AccountLayout>} />
      <Route path="adresses" element={<AccountLayout title={t('acc.addresses')}><AddressBook /></AccountLayout>} />
      <Route path="banque" element={<AccountLayout title={t('acc.bank')}><BankDetails /></AccountLayout>} />
      <Route path="depot" element={<AccountLayout title={t('acc.wallet')}><WalletSection /></AccountLayout>} />
      <Route path="parametres" element={<AccountLayout title={t('acc.settings')}><AccountSettings /></AccountLayout>} />
      <Route path="notifications" element={<AccountLayout title={t('acc.notifications')}><NotificationsCenter /></AccountLayout>} />
      <Route path="alertes" element={<AccountLayout title={t('acc.alerts')}><MyAlertsSection /></AccountLayout>} />
      <Route path="parrainage" element={<AccountLayout title={t('acc.referral')}><ReferralSection /></AccountLayout>} />
    </Routes>
  );
};

export default AccountPage;
